import { Badge } from "../../ui/badge";

export const today = () => new Date().toISOString().slice(0, 10);

export const STATUS_META = {
  not_contacted: { label: "Not Contacted", color: "#8A857A" },
  demo_requested: { label: "Demo Requested", color: "#EA580C", solid: true },
  emailed: { label: "Emailed", color: "#8A6D00" },
  called: { label: "Called", color: "#1D4ED8" },
  responded: { label: "Responded", color: "#FFD108" },
  meeting_booked: { label: "Meeting Booked", color: "#6B5600" },
  demo_completed: { label: "Demo Completed", color: "#3B82F6" },
  quote_sent: { label: "Quote Sent · Pending Decision", color: "#B45309" },
  trialing: { label: "Trialing", color: "#6B5600" },
  converted: { label: "Converted", color: "#0E6B3F" },
  disqualified: { label: "Disqualified", color: "#C23A00" },
};
export const STATUS_ORDER = ["not_contacted", "demo_requested", "emailed", "called", "responded", "meeting_booked", "demo_completed", "quote_sent", "trialing", "converted", "disqualified"];

/* --- Directive §5.3 / §10.3: pipeline stage tone + milestone (M1/M2 n/N) model --- */
// Stage → tone map for the Status column. Quiet stages render dot + plain label.
export const STATUS_TONE = {
  not_contacted: { quiet: true, label: "Not contacted" },
  emailed: { quiet: true, label: "Emailed" },
  called: { quiet: true, label: "Called" },
  demo_requested: { label: "Demo requested", fill: "#FFF7D1", ink: "#6B5600", border: "#E8D48A", dot: "#E0B800" },
  responded: { label: "Responded", fill: "#EFF6FF", ink: "#1D4ED8", border: "#BFDBFE", dot: "#3B82F6" },
  meeting_booked: { label: "Meeting booked", fill: "#E3F3E9", ink: "#0B4F2E", border: "#A9D9BE", dot: "#0E6B3F" },
  demo_completed: { label: "Demo completed", fill: "#EFF6FF", ink: "#1D4ED8", border: "#BFDBFE", dot: "#3B82F6" },
  quote_sent: { label: "Quote sent · pending decision", fill: "#FEF3C7", ink: "#92400E", border: "#FDE68A", dot: "#D97706" },
  trialing: { label: "Trialing", fill: "#FFF7D1", ink: "#6B5600", border: "#E8D48A", dot: "#E0B800" },
  converted: { label: "Converted", fill: "#E3F3E9", ink: "#0B4F2E", border: "#A9D9BE", dot: "#0E6B3F" },
  disqualified: { label: "Disqualified", fill: "#FBFAF7", ink: "#8F8A7F", border: "#E2DED4", dot: "#B5AFA3" },
};

export function StatusCell({ status }) {
  const t = STATUS_TONE[status] || { quiet: true, label: status || "—" };
  if (t.quiet) {
    return (
      <span className="inline-flex items-center gap-1.5" data-testid="status-quiet">
        <span className="rounded-full" style={{ width: 5, height: 5, background: "#8A857A" }} />
        <span className="text-[12.5px] text-[#59554D]">{t.label}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="status-pill"
      style={{ background: t.fill, color: t.ink, border: `1px solid ${t.border}`, borderRadius: 999, padding: "2px 9px" }}>
      <span className="rounded-full" style={{ width: 5, height: 5, background: t.dot }} />
      <span className="text-[10.5px] font-bold tracking-[0.04em]">{t.label}</span>
    </span>
  );
}

// Milestone step model (derived purely from status). M1·To demo = 5 steps, M2·To client = 4 steps.
export const STAGE_STEPS = {
  not_contacted: { label: "M1 · To demo", step: 1, total: 5 },
  emailed: { label: "M1 · To demo", step: 2, total: 5 },
  called: { label: "M1 · To demo", step: 3, total: 5 },
  responded: { label: "M1 · To demo", step: 4, total: 5 },
  demo_requested: { label: "M1 · To demo", step: 5, total: 5 },
  meeting_booked: { label: "M2 · To client", step: 1, total: 5 },
  demo_completed: { label: "M2 · To client", step: 2, total: 5 },
  quote_sent: { label: "M2 · To client", step: 3, total: 5 },
  trialing: { label: "M2 · To client", step: 4, total: 5 },
  converted: { label: "M2 · To client", step: 5, total: 5 },
};
export const stageFor = (status) => STAGE_STEPS[status] || null;

// QUOTE DUE safety marker: demo completed but no quote yet in the journey.
export const isQuoteDue = (p) =>
  p?.status === "demo_completed" && !((p?.journey?.events || []).some((e) => e.tone === "quote" || e.kind === "quote"));

// Whole days overdue for a next-step date (0 if future/absent).
export const overdueDays = (dateStr) => {
  if (!dateStr) return 0;
  const d = new Date(dateStr); const t = new Date();
  d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0);
  const diff = Math.floor((t - d) / 86400000);
  return diff > 0 ? diff : 0;
};


export const SIGNUP_PRODUCT_META = {
  core: { label: "Core", color: "#8A6D00" },
  guardian: { label: "Guardian", color: "#1D4ED8" },
};
export const SIGNUP_LIFECYCLE_META = {
  trial: { label: "Trial", color: "#6B5600" },
  active: { label: "Paying", color: "#0E6B3F" },
  churned: { label: "Churned", color: "#8A857A" },
};

export function SignupBadge({ prospect }) {
  const prod = SIGNUP_PRODUCT_META[prospect?.signup_product];
  if (!prod) return null;
  const life = SIGNUP_LIFECYCLE_META[prospect?.signup_lifecycle] || SIGNUP_LIFECYCLE_META.trial;
  return (
    <span className="inline-flex items-center gap-1" data-testid={`signup-badge-${prospect.id}`}>
      <Badge className="border-0 text-[10px] font-medium" style={{ background: `${prod.color}1A`, color: prod.color }}>
        EQUIPR {prod.label}
      </Badge>
      <Badge className="border-0 text-[10px] font-medium" style={{ background: `${life.color}1A`, color: life.color }}>
        {life.label}
      </Badge>
    </span>
  );
}

export const ACTIVITY_TYPES = ["email", "call", "meeting", "note", "linkedin", "sms", "inbound", "other"];
export const CALL_OUTCOME_LABELS = {
  connected: "Connected — spoke with them",
  no_answer: "No answer",
  left_voicemail: "Left voicemail",
  callback_requested: "Callback requested",
  wrong_number: "Wrong / bad number",
};
export const CALL_OUTCOME_ORDER = ["connected", "no_answer", "left_voicemail", "callback_requested", "wrong_number"];
export const NEXT_ACTION_LABELS = {
  call_again: "Call again",
  send_couldnt_reach_email: "Send couldn't-reach email",
  call_back: "Call back",
  fix_contact: "Fix contact details",
  meeting_followup: "Demo follow-up",
};

export function StatusBadge({ status, testid }) {
  const m = STATUS_META[status] || STATUS_META.not_contacted;
  const style = m.solid
    ? { background: m.color, color: "#FFFFFF" }
    : { background: `${m.color}1A`, color: m.color };
  return (
    <Badge className="border-0 font-semibold" style={style} data-testid={testid}>
      {m.label}
    </Badge>
  );
}

export const emptyProspect = {
  company_name: "", category: "", priority_tier: "", client_network_count: "0",
  contact_name: "", contact_email: "", contact_phone: "", suburb: "", country: "Australia", currency: "AUD", source: "",
  assigned_rep: "", current_sequence_step: "", next_action_date: "", status: "not_contacted", notes: "",
};
